import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, PagedResult } from '../models/product.models';
import {
  OrderDto,
  OrderDetailDto,
  ProfitReportDto,
  DebtDto,
  CreateOrderRequest,
  DebtorDto,
} from '../models/order.models';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class OrderService extends ApiService {
  private readonly endpoint = `${environment.apiUrl}/api/Orders`;

  getOrders(
    page: number,
    pageSize: number,
    storeIds?: string[],
    paymentType?: string,
    from?: string,
    to?: string,
    search?: string,
  ): Observable<ApiResponse<PagedResult<OrderDto>>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (paymentType) params = params.set('paymentType', paymentType);
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);
    if (search) params = params.set('search', search);
    return this.http.get<ApiResponse<PagedResult<OrderDto>>>(this.endpoint, { params });
  }

  getById(id: string): Observable<ApiResponse<OrderDetailDto>> {
    return this.http.get<ApiResponse<OrderDetailDto>>(`${this.endpoint}/${id}`);
  }

  /**
   * @param currency Faqat shu valyutadagi qarzlar. Berilmasa — hammasi.
   */
  getDebts(
    page: number,
    pageSize: number,
    storeIds?: string[],
    search?: string,
    customerId?: string,
    currency?: string,
  ): Observable<ApiResponse<PagedResult<DebtDto>>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (search) params = params.set('search', search);
    if (customerId) params = params.set('customerId', customerId);
    if (currency) params = params.set('currency', currency);
    return this.http.get<ApiResponse<PagedResult<DebtDto>>>(`${this.endpoint}/debts`, { params });
  }

  /**
   * Qarzdorlar — mijoz VA valyuta bo'yicha guruhlangan. Ikki valyutada qarzi bor
   * mijoz ikki qator bo'lib keladi; summalar hech qachon qo'shilmaydi.
   *
   * @param currency Faqat shu valyuta. Berilmasa — hamma valyutadagi qarzlar.
   */
  getDebtors(
    page: number,
    pageSize: number,
    storeIds?: string[],
    search?: string,
    currency?: string,
  ): Observable<ApiResponse<PagedResult<DebtorDto>>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (search) params = params.set('search', search);
    if (currency) params = params.set('currency', currency);
    return this.http.get<ApiResponse<PagedResult<DebtorDto>>>(`${this.endpoint}/debtors`, {
      params,
    });
  }

  /**
   * Qarzga to'lov.
   *
   * @param amount Mijoz bergan REAL summa — `currency` da.
   * @param currency
   *   Mijoz qaysi pulni bergani. Qarz valyutasidan farq qilishi mumkin
   *   (`$100` lik qarzni so'mda yopish). Berilmasa order valyutasi.
   * @param rate
   *   1 birlik qarz valyutasi = shuncha `currency`. Ekranda ko'rsatilgan kurs
   *   AYNAN shu yerda uzatiladi — server uni qayta hisoblamaydi, aks holda
   *   foydalanuvchi ko'rgan summa bazadagidan farq qilardi.
   * @param method
   *   Pul QANDAY qabul qilingani: 0=Naqd, 1=Karta, 2=Bank (`PaymentMethod` enum
   *   bilan bir xil sonlar). Berilmasa naqd — kassa/karta/bank qoldig'i shu
   *   maydonga qarab hisoblanadi, xato qiymat pulni noto'g'ri hisobga yozadi.
   */
  payDebt(
    id: string,
    amount: number,
    cashierId: string,
    currency?: string,
    rate?: number,
    method?: 0 | 1 | 2,
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.endpoint}/${id}/pay-debt`, {
      amount,
      cashierId,
      currency,
      rate,
      method,
    });
  }

  /**
   * Backend `{ orderId }` qaytaradi (`OrdersController.Post`) — `id` EMAS.
   * Ilgari bu yerda `{ id }` deb yozilgan edi va hech kim o'qimagani uchun
   * xato bilinmagan; chekni Excel qilish uchun id kerak bo'lgach chiqdi.
   */
  create(request: CreateOrderRequest): Observable<ApiResponse<{ orderId: string }>> {
    return this.http.post<ApiResponse<{ orderId: string }>>(this.endpoint, request);
  }

  getProfitReport(
    from: string,
    to: string,
    storeIds?: string[],
    /** Bo'sh bo'lsa backend tenant kotirovka valyutasini oladi (I11). */
    currency?: string,
  ): Observable<ApiResponse<ProfitReportDto>> {
    let params = new HttpParams().set('from', from).set('to', to);
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (currency) params = params.set('currency', currency);
    return this.http.get<ApiResponse<ProfitReportDto>>(`${this.endpoint}/profit-report`, {
      params,
    });
  }
}
